import React, { useCallback, useMemo } from 'react';
import I18N from '@leverege/i18n';
import { Button, Flex, Pane, Text } from '@leverege/ui-elements';
import { AttributeConditionEditor, AttributeCondition } from '@leverege/ui-attributes';
import { SharedContext } from '@leverege/ui-plugin';
import { LogicCondition } from '@leverege/condition';
import { GroupDelegate } from '@leverege/molten-core';
import S from './SimpleFilterAction.css';
import { FilterSourceModel, FilterSourcesModel } from "../../../filters/index.js";
import FilterSortMetadataModel from "../../../filters/filter/FilterSortMetadataModel.js";
import ConditionFilterModel from "../../../filters/filter/ConditionFilterModel.js";
import ConditionFilterOptionsCreators from "../../../filters/filter/ConditionFilterOptionsCreators.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tf,
  tIcon,
  tfIcon
} = I18N.ns('molten.dataViewers.table.SimpleFilterAction');
const FILTER_NAME = 'filterBar';
const EMPTY_ARR = [];
export default {
  id: 'molten.dataViewers.table.SimpleFilterAction',
  matches: {
    use: 'tableColumnHeaderActionBar',
    client: 'TableDataViewer'
  },
  handles: () => true,
  layout: {
    sort: tf('sortValue'),
    group: [tf('filter')],
    groupIcons: [tfIcon('filterMenuIcon', 'fa fa-fw fa-chevron-down')]
  },
  customComponent: ActionSwitch
};
function ActionSwitch(props) {
  const {
    context
  } = props;
  const {
    objectType
  } = context;
  if (objectType?.endsWith('.history')) {
    return /*#__PURE__*/_jsx(HistoryAction, {
      ...props
    });
  }
  return /*#__PURE__*/_jsx(Action, {
    ...props
  });
}
function HistoryAction({
  context,
  onClose
}) {
  // TODO: Add simple filters to history table
  return null;
}
function Action({
  context,
  onClose
}) {
  const {
    canFilter,
    delegate,
    filters,
    attribute,
    relationship,
    onFilterChange
  } = context;
  const topLevelFilters = FilterSourcesModel.getSource(filters, FILTER_NAME)?.metadata?.filter?.rootCondition?.conditions || EMPTY_ARR;

  // eslint-disable-next-line prefer-const
  let {
    filter,
    filterIndex,
    isAdvanced
  } = useMemo(() => {
    let isAdvanced = false;
    let filterIndex = -1;
    let filter;
    for (let i = 0; i < topLevelFilters.length; i++) {
      const item = topLevelFilters[i];
      if (item.attrName === attribute.name) {
        if (filterIndex > -1) {
          isAdvanced = true;
          filterIndex = -1;
          filter = null;
          break;
        }
        filterIndex = i;
        filter = item;
      }
    }
    return {
      filter,
      filterIndex,
      isAdvanced
    };
  }, [topLevelFilters, attribute?.name]);
  const mode = filter ? 'update' : 'create';
  filter = filter || AttributeCondition.create({
    attrName: attribute.name
  });
  const onClickAdvanced = useCallback(() => {
    window.dispatchEvent(new CustomEvent(`molten.filter.${attribute.objectType}.open`, {
      detail: {
        attrName: attribute.name,
        objectType: attribute.objectType
      }
    }));
    onClose();
  }, [onClose, attribute]);
  const onChange = useCallback(({
    value,
    data
  }) => {
    let logicCondition = FilterSourcesModel.getSource(filters, FILTER_NAME)?.metadata?.filter?.rootCondition || LogicCondition.create({
      operator: 'and'
    });
    if (mode === 'create') {
      logicCondition = LogicCondition.addCondition(logicCondition, value);
    } else {
      logicCondition = LogicCondition.setCondition(logicCondition, data, value);
    }
    const filter = FilterSourcesModel.getSource(filters, FILTER_NAME) || FilterSourceModel.create();
    const conditionFilterModel = ConditionFilterModel.setRootCondition(FilterSortMetadataModel.getFilter(filter.metadata || {}) || ConditionFilterModel.create(), logicCondition);
    const conditionFilter = delegate?.convertToFilter?.(conditionFilterModel, context);
    const metadata = FilterSortMetadataModel.setFilter(filter.metadata || {}, conditionFilterModel);
    let filterModel = FilterSourceModel.setMetadata(filter, metadata);
    filterModel = FilterSourceModel.setFilter(filterModel, conditionFilter);
    onFilterChange({
      data: FILTER_NAME,
      value: filterModel
    });
  }, [mode, filters, context, delegate, onFilterChange]);
  const onClear = useCallback(({
    data
  }) => {
    let logicCondition = FilterSourcesModel.getSource(filters, FILTER_NAME)?.metadata?.filter?.rootCondition;
    if (!logicCondition) {
      return;
    }
    logicCondition = LogicCondition.removeConditionAt(logicCondition, data);
    const filter = FilterSourcesModel.getSource(filters, FILTER_NAME) || FilterSourceModel.create();
    const conditionFilterModel = ConditionFilterModel.setRootCondition(FilterSortMetadataModel.getFilter(filter.metadata || {}) || ConditionFilterModel.create(), logicCondition);
    const conditionFilter = delegate?.convertToFilter?.(conditionFilterModel, context);
    const metadata = FilterSortMetadataModel.setFilter(filter.metadata || {}, conditionFilterModel);
    let filterModel = FilterSourceModel.setMetadata(filter, metadata);
    filterModel = FilterSourceModel.setFilter(filterModel, conditionFilter);
    onFilterChange({
      data: FILTER_NAME,
      value: filterModel
    });
  }, [filters, onFilterChange, context, delegate]);
  if (!canFilter || delegate?.type !== GroupDelegate.TYPE) {
    return null;
  }
  if (isAdvanced) {
    return /*#__PURE__*/_jsx(Pane, {
      variant: "simpleFilterEditorWrapper",
      className: S.action,
      children: /*#__PURE__*/_jsxs(Flex, {
        variant: "colM",
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "info",
          children: t('info')
        }), /*#__PURE__*/_jsx(Button, {
          variant: "secondary",
          icon: tIcon('advancedFilterActionIcon'),
          onClick: onClickAdvanced,
          children: t('openAdvancedFilters')
        })]
      })
    });
  }
  return /*#__PURE__*/_jsx(Pane, {
    variant: "simpleFilterEditorWrapper",
    className: S.action,
    children: /*#__PURE__*/_jsx(Pane, {
      variant: "simpleFilterEditor",
      children: /*#__PURE__*/_jsx(Flex, {
        variant: "colM",
        children: /*#__PURE__*/_jsx(Editor, {
          mode: mode,
          value: filter,
          filterIndex: filterIndex,
          attribute: attribute,
          delegate: delegate,
          relationship: relationship,
          eventData: filterIndex,
          onChange: onChange,
          onClear: onClear
        })
      })
    })
  });
}
function Editor(props) {
  const {
    onChange,
    onClear,
    eventData,
    value,
    filterIndex,
    attribute,
    delegate,
    relationship
  } = props;
  const optionsCreator = ConditionFilterOptionsCreators.getOptionsCreatorFor(delegate.type, attribute.objectType);
  const {
    creators,
    conditionOptions
  } = optionsCreator?.create?.(props) || {};
  return /*#__PURE__*/_jsx(SharedContext, {
    conditionOptions: conditionOptions,
    conditionCreators: creators,
    objectType: attribute.objectType,
    delegate: delegate,
    relationship: relationship,
    children: /*#__PURE__*/_jsxs(Flex, {
      variant: "colM",
      children: [/*#__PURE__*/_jsxs(Flex, {
        variant: "rowSpaceBetween",
        align: "center",
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "tableConditionAttributeLabel|conditionAttributeLabel|bold",
          children: attribute.displayName
        }), filterIndex !== -1 ? /*#__PURE__*/_jsx(Button, {
          eventData: filterIndex,
          variant: "linkNeutral",
          icon: tIcon('close'),
          onClick: onClear
        }) : null]
      }), /*#__PURE__*/_jsx(AttributeConditionEditor, {
        usage: "table",
        eventData: eventData,
        value: value,
        onChange: onChange
      })]
    })
  });
}